import { UseChat, UseGroups } from '@core/usecases';
import { ElementRef, inject } from '@angular/core';
import { untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { ChatEntity, ChatMessageEntity, ChatMessagePayload, GroupEntity, UserEntity } from '@core/entities';
import { ChatMessageType, ChatType, ConfirmationDialogType, DeleteConfirmationOptions, SocketEvents } from '@core/enums';
import { FormService, Logger } from '@core/services';
import { GetUserId, isMobileView, removeNullAndUndefinedProperties } from '@core/utils';
import { lastValueFrom, mergeMap } from 'rxjs';
import { SocketIoService } from '@core/socket-io';
import { SafeUrl } from '@angular/platform-browser';
import { DefaultDeleteDialogConfig, MDeleteConfirmationDialogComponent } from '@mobile/_shared/dialogs';
import { ConfirmationDialogComponent } from '@web/_shared/dialogs';
import { DialogService } from '@ngneat/dialog';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';
import { HotToastService } from '@ngxpert/hot-toast';
import { ActivatedRoute, Router } from '@angular/router';
import { PERMISSIONS, PermissionService } from '@auth';
import { Theme, ThemeService } from '@app/shell/services/theme.service';
import { ChatFormComponent } from '../components/forms/chat-form/chat-form.component';
import { PreviewFile, PreviewType } from '@eternalheart/ngx-file-preview';
import { saveAs } from 'file-saver';

const log = new Logger('Chats Composition');

export class ChatsComposition extends FormService {
  filteredConversationsList: ChatEntity[] = []; // Filtered chat list if user search for chat
  groups: GroupEntity[] = [];
  loadings = {
    conversations: true,
    messages: true,
  };

  // ------- Chat Input Fields --------
  selectedAttachment: File;
  selectedImagePreviewUrl: SafeUrl | string;
  messageInput = '';
  // ----------------------------------

  // Show Image In Lightbox
  showLightBox = false;
  attachmentImages: string[] = [];

  selectedConversation: ChatEntity | null = null;
  selectedConversationChatMessages: ChatMessageEntity[] = [];
  selectedConversationGroupParticipants: UserEntity[] = [];
  selectedConversationMediaMessages: ChatMessageEntity[] = [];
  selectedConversationCommonGroups: GroupEntity[] = [];
  roomId: string;

  protected readonly ChatType = ChatType;
  protected readonly ChatMessageType = ChatMessageType;
  protected readonly _translate: TranslateService = inject(TranslateService);
  protected readonly _socketService: SocketIoService = inject(SocketIoService);
  protected readonly _router: Router = inject(Router);
  protected _chatComponentInstance: any;
  protected readonly _bottomSheetService = inject(NgxBottomSheetService);
  protected readonly _activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  protected readonly _themeService: ThemeService = inject(ThemeService);
  protected _routeGroupId: number; // variable to store group id passed in query param passed from group screen when clicked on chat icon
  protected _routeUserId: number; // variable to store user id passed in query param passed from user/groups screen when clicked on chat icon
  protected readonly _useChat: UseChat = new UseChat();

  private _chatsList: ChatEntity[] = [];
  private readonly _dialogService = inject(DialogService);
  private readonly _toastService = inject(HotToastService);
  private readonly _permissionService = inject(PermissionService);

  private readonly _useGroup: UseGroups = new UseGroups();

  private get _chatScrollContainer(): ElementRef | null {
    return this._chatComponentInstance ? this._chatComponentInstance._myScrollContainer : null;
  }

  get isLastMessageFromOtherUser(): boolean {
    return this.selectedConversation.lastMessage.senderUid !== +GetUserId();
  }

  get selectedAttachmentFileName(): string {
    return this.selectedAttachment ? this.selectedAttachment.name : '';
  }

  get chatCreatePermission(): boolean {
    return this._permissionService.hasPermission(PERMISSIONS.CREATE_CHAT);
  }

  get isAllowedToDeleteMessage(): boolean {
    return this._permissionService?.isAdmin() || this._permissionService.isSecretary() || this._permissionService.isEditor();
  }

  get previewThemeMode(): 'light' | 'dark' {
    return this._themeService.currentTheme === Theme.Dark ? 'dark' : 'light';
  }

  get previewLang(): string {
    const lang = (this._translate.currentLang || this._translate.getDefaultLang() || 'en').toLowerCase();
    return lang.startsWith('zh') ? 'zh' : 'en';
  }

  getGroupParticipantMemberId(message: ChatMessageEntity): number {
    return +this.selectedConversationGroupParticipants?.find((participant) => participant.id === +message.senderId)?.memberId || 0;
  }

  getGroupParticipantName(message: ChatMessageEntity): string {
    return this.selectedConversationGroupParticipants?.find((participant) => participant.id === +message.senderId)?.fullName || '- -';
  }

  isNewDate(sentAt: string, currentIndex: number): boolean {
    return currentIndex === 0 || new Date(sentAt).toDateString() !== new Date(this.selectedConversationChatMessages[currentIndex - 1].message.timestamp).toDateString();
  }

  isUserFirstMessage(currentIndex: number): boolean {
    return currentIndex === 0 || this.selectedConversationChatMessages[currentIndex].senderId !== this.selectedConversationChatMessages[currentIndex - 1].senderId;
  }

  isUserLastMessage(currentIndex: number): boolean {
    const lastIndex = this.selectedConversationChatMessages.length - 1;
    return currentIndex === lastIndex || this.selectedConversationChatMessages[currentIndex].senderId !== this.selectedConversationChatMessages[currentIndex + 1].senderId;
  }

  searchConversation(event: any) {
    this.filteredConversationsList = this._chatsList.filter((chat) => chat.fullName.toLowerCase().includes(event.target.value.toLowerCase()));
    if (!event.target.value) {
      this.filteredConversationsList = this._chatsList;
    }
  }

  openImagePreview(url: string) {
    this.attachmentImages = [url];
    this.showLightBox = true;
  }

  selectConversation(conversation: ChatEntity) {
    const roomUsers = [+GetUserId(), +conversation.id].sort((a, b) => a - b);
    this.roomId = conversation.type === ChatType.PERSONAL_CHAT ? `${roomUsers[0]},${roomUsers[1]}` : conversation.id.toString();
    this.loadings.messages = true;
    this.selectedConversation = conversation;
    if (this.selectedConversation.type === ChatType.GROUP_CHAT) {
      this._fetchGroupParticipants(+conversation.id);
    } else {
      this.selectedConversationGroupParticipants = null;
    }

    this._joinChatRoom(conversation);
  }

  sendMessageOld() {
    if (!this.selectedAttachment && !this.messageInput) return;
    this.isLoading = true;

    let payload: ChatMessagePayload = {
      file: this.selectedAttachment ? this.selectedAttachment : null,
      message: this.messageInput ? { message: this.messageInput.trim() } : null,
      friendUid: +this.selectedConversation.id,
      currentUid: +GetUserId(),
      roomId: this.roomId,
    };

    payload = removeNullAndUndefinedProperties(payload);

    const formData = this.jsonToFromData(payload, ['message']);
    formData.delete('file');
    if (this.selectedAttachment) {
      formData.append('file', this.selectedAttachment);
    }

    this._useChat
      .saveChatMessage(formData)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (resp: any) => {
          this._socketService.emit(SocketEvents.ChatMessageSent, payload, (error: any) => {
            if (error) {
              log.error('Error sending message:', error);
            } else {
              log.info('Message sent successfully');
            }
          });
          this._resetChatInputFields();
          this.isLoading = false;
        },
        error: (error: any) => {
          log.error('Error sending message:', error);
          this._toastService.error(error?.error?.message || this._translate.instant('Something went wrong'));
          this.isLoading = false;
        },
      });
  }

  sendMessage() {
    if (!this.selectedAttachment && !this.messageInput) return;
    this.isLoading = true;

    // Simplified payload creation
    const basePayload = {
      friendUid: +this.selectedConversation.id,
      currentUid: +GetUserId(),
      roomId: this.roomId,
    };

    if (this.selectedAttachment && this.messageInput) {
      // Create separate formData for each request
      const imageFormData = new FormData();
      const textFormData = new FormData();

      // Populate the form data objects
      Object.entries(basePayload).forEach(([key, value]) => {
        imageFormData.append(key, value as string);
        textFormData.append(key, value as string);
      });

      if (this.messageInput) {
        textFormData.append('message', JSON.stringify({ message: this.messageInput.trim() }));
      }

      if (this.selectedAttachment) {
        imageFormData.append('file', this.selectedAttachment);
      }

      // Use mergeMap instead of forkJoin to ensure sequential processing
      this._useChat
        .saveChatMessage(imageFormData)
        .pipe(
          mergeMap(() => this._useChat.saveChatMessage(textFormData)),
          untilDestroyed(this),
        )
        .subscribe({
          next: () => {
            // Emit socket event with combined info for notification
            const notificationPayload = {
              ...basePayload,
              message: { message: this.messageInput.trim() },
              file: this.selectedAttachment,
            };

            this._socketService.emit(SocketEvents.ChatMessageSent, notificationPayload, (error: any) => {
              if (error) {
                log.error('Error sending message:', error);
              } else {
                log.info('Message sent successfully');
              }
            });

            this._resetChatInputFields();
            this.isLoading = false;
          },
          error: (error: any) => {
            log.error('Error sending message:', error);
            this._toastService.error(error?.error?.message || this._translate.instant('Something went wrong'));
            this.isLoading = false;
          },
        });
    } else {
      // Handle single message case (text only or image only)
      const formData = new FormData();

      // Add base payload properties
      Object.entries(basePayload).forEach(([key, value]) => {
        formData.append(key, value as string);
      });

      if (this.messageInput) {
        formData.append('message', JSON.stringify({ message: this.messageInput.trim() }));
      }

      if (this.selectedAttachment) {
        formData.append('file', this.selectedAttachment);
      }

      this._useChat
        .saveChatMessage(formData)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: () => {
            const notificationPayload = {
              ...basePayload,
              message: this.messageInput ? { message: this.messageInput.trim() } : null,
              file: this.selectedAttachment || null,
            };

            this._socketService.emit(SocketEvents.ChatMessageSent, notificationPayload, (error: any) => {
              if (error) {
                log.error('Error sending message:', error);
              } else {
                log.info('Message sent successfully');
              }
            });

            this._resetChatInputFields();
            this.isLoading = false;
          },
          error: (error: any) => {
            log.error('Error sending message:', error);
            this._toastService.error(error?.error?.message || this._translate.instant('Something went wrong'));
            this.isLoading = false;
          },
        });
    }
  }

  openFileSelector() {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    // fileInput.accept = 'image/*'; // Only allow image files for now

    fileInput.addEventListener('change', (event: any) => {
      if (event.target.files[0].size > 10485760) {
        this._toastService.error('File size too large. Maximum file size is 10MB.');
        return;
      }

      this.selectedAttachment = event.target.files[0];

      if (event.target.files[0] && event.target.files[0].type.startsWith('image/')) {
        this.selectedImagePreviewUrl = URL.createObjectURL(this.selectedAttachment);
      } else {
        log.warn('Selected file is not an image.');
        this.selectedImagePreviewUrl = null;
      }
    });

    // Trigger the click event to open the file selector
    fileInput.click();
  }

  isImage(url: string): boolean {
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'avif', 'apng', 'jfif', 'pjpeg', 'pjp']; // Add more if needed
    const extension = this._getFileExtension(url).toLowerCase();
    return imageExtensions?.includes(extension);
  }

  getPreviewFile(url: string): PreviewFile {
    if (!url) {
      return {
        url: '',
        name: '',
        type: 'unknown',
      };
    }

    return {
      url,
      name: this.getDocFileName(url),
      type: this._getPreviewType(url),
    };
  }

  getDocFileName(url: any) {
    const parts = decodeURIComponent(url).split('/');
    const fileNameWithQuery = parts[parts.length - 1];
    const fileNameParts = fileNameWithQuery.split('?');
    return fileNameParts[0];
  }

  downloadDocument(url: string) {
    try {
      const filename = this.getDocFileName(url);

      // Try fetch first (works if file is small and CORS allows)
      // If it fails (CORS), fall back to anchor element approach
      fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error('Network response was not ok');
          return response.blob();
        })
        .then((blob) => {
          // Successfully fetched - use file-saver for download
          saveAs(blob, filename);
          log.info('File downloaded: ' + filename);
        })
        .catch(() => {
          // CORS or network error - use anchor element fallback
          // Works on PWA (iOS/Android) without opening browser tab
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          link.style.display = 'none';

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          log.info('Download started (fallback method): ' + filename);
        });
    } catch (err) {
      log.error('Download error:', err);
      this._toastService.error(this._translate.instant('Failed to download file'));
    }
  }

  deleteConversation() {
    if (!this.selectedConversation || !this.roomId) return;

    let confirmationDelete: any;
    if (isMobileView()) {
      confirmationDelete = this._bottomSheetService.open(MDeleteConfirmationDialogComponent, {
        ...DefaultDeleteDialogConfig,
        data: {
          deleteText: this._translate.instant('Delete Chat'),
          showReportButton: false,
        },
      });
    } else {
      confirmationDelete = this._dialogService.open(ConfirmationDialogComponent, {
        size: 'md',
        closeButton: false,
        windowClass: 'dialog-overflow-unset',
        data: {
          type: ConfirmationDialogType.DELETE,
        },
      });
    }

    confirmationDelete.afterClosed$.pipe(untilDestroyed(this)).subscribe({
      next: (result: any) => {
        if ((!isMobileView() && result) || (isMobileView() && result === DeleteConfirmationOptions.DELETE)) {
          this._useChat
            .deleteConversation(this.roomId)
            .pipe(untilDestroyed(this))
            .subscribe({
              next: (respons: any) => {
                // this._toastService.success(this._translate.instant('Chat deleted successfully'));

                if (isMobileView()) {
                  this._router.navigate(['/m/messages']);
                }

                this._chatsList = this._chatsList.filter((chat) => chat.id !== this.selectedConversation.id);
                this.selectedConversation = null;
                this.selectedConversationChatMessages = [];
                this.selectedConversationGroupParticipants = [];
                this.selectedConversationMediaMessages = [];
                this.selectedConversationCommonGroups = [];
                this.roomId = null;
                this._resetChatInputFields();
                this._socketService.emit(SocketEvents.ChatMessageSent, '', (error: any) => {
                  if (error) {
                    log.error('Error deleting conversation:', error);
                  } else {
                    log.info('Conversation deleted successfully');
                  }
                });
              },
            });
        }
      },
    });
  }

  deleteChatMessage(message: ChatMessageEntity) {
    if (!message) return;

    if (isMobileView()) {
      this._bottomSheetService
        .open(MDeleteConfirmationDialogComponent, {
          ...DefaultDeleteDialogConfig,
          data: {
            text: this._translate.instant('Message'),
            showReportButton: false,
          },
        })
        .afterClosed$.pipe(untilDestroyed(this))
        .subscribe({
          next: (result: any) => {
            if (result && result === DeleteConfirmationOptions.DELETE) {
              this._useChat
                .deleteChatMessage(message.id)
                .pipe(untilDestroyed(this))
                .subscribe({
                  next: (respons: any) => {
                    // this._toastService.success(this._translate.instant('Message deleted successfully'));
                    this.selectedConversationChatMessages = this.selectedConversationChatMessages.filter((msg) => msg.id !== message.id);
                    this._socketService.emit(SocketEvents.ChatMessageSent, '', (error: any) => {
                      if (error) {
                        log.error('Error deleting conversation:', error);
                      } else {
                        log.info('Conversation deleted successfully');
                      }
                    });
                    this._fetchConversations();
                  },
                });
            }
          },
        });
    } else {
      this._dialogService
        .open(ConfirmationDialogComponent, {
          size: 'md',
          closeButton: false,
          windowClass: 'dialog-overflow-unset',
          data: {
            type: ConfirmationDialogType.DELETE,
          },
        })
        .afterClosed$.pipe(untilDestroyed(this))
        .subscribe({
          next: (result: any) => {
            if (result) {
              this._useChat
                .deleteChatMessage(message.id)
                .pipe(untilDestroyed(this))
                .subscribe({
                  next: (respons: any) => {
                    // this._toastService.success(this._translate.instant('Message deleted successfully'));
                    this.selectedConversationChatMessages = this.selectedConversationChatMessages.filter((msg) => msg.id !== message.id);
                    this._socketService.emit(SocketEvents.ChatMessageSent, '', (error: any) => {
                      if (error) {
                        log.error('Error deleting conversation:', error);
                      } else {
                        log.info('Conversation deleted successfully');
                      }
                    });
                    this._fetchConversations();
                  },
                });
            }
          },
        });
    }
  }

  deleteMultipleChatMessages(ids: number[]) {
    if (isMobileView()) {
      this._bottomSheetService
        .open(MDeleteConfirmationDialogComponent, {
          ...DefaultDeleteDialogConfig,
          data: {
            text: this._translate.instant('header.messages'),
            showReportButton: false,
          },
        })
        .afterClosed$.pipe(untilDestroyed(this))
        .subscribe({
          next: (result: any) => {
            if (result && result === DeleteConfirmationOptions.DELETE) {
              this._useChat
                .deleteMultipleChatMessages(ids)
                .pipe(untilDestroyed(this))
                .subscribe({
                  next: (response) => {
                    this._fetchConversationMessages(this.selectedConversation);
                  },
                  error: (error) => {
                    log.error('Error deleting multiple messages:', error);
                  },
                });
            }
          },
        });
    }
  }

  // Function only for new chat creation. When a user create new chat and save it, this function will be called to fetch the conversations and select the first conversation
  fetchConversationsAndSelectFirstConversation(selectFirstConversation = true) {
    this._fetchConversations(selectFirstConversation);
  }

  markChatsSectionAsRead() {
    this._useChat.markChatsAsRead().pipe(untilDestroyed(this)).subscribe();
  }

  /**
   * Fetch conversation messages and mark the conversation as read
   * @param conversation
   * @protected
   */
  protected _fetchConversationMessages(conversation: ChatEntity) {
    this._fetchConversationMessagesAsPromise()
      .then((response) => {
        this.selectedConversationChatMessages = response;
        this.loadings.messages = false;
        conversation.count = 0;
        this._scrollToBottom();
        this._filterMediaMessages();
        if (conversation.type === ChatType.PERSONAL_CHAT) {
          this._fetchCommonGroups(+conversation.id);
        }
      })
      .catch((error) => {
        log.error('Error fetching conversation messages:', error);
        this.loadings.messages = false;
      });
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------ Private Protected Functions ----------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  /**
   * Fetch Conversations/Chat Lists
   * @protected
   */
  protected _fetchConversations(selectFirstConversation = false) {
    this._useChat
      .getConversationsList()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (response: ChatEntity[]) => {
          this._chatsList = response;

          if (selectFirstConversation && this._chatsList.length) {
            const chat = this._chatsList[0];
            if (isMobileView()) {
              // TODO: if group name dosent appear in chat detail screen on production then uncomment this below code
              // if (chat.type === ChatType.Group) {
              //   return;
              // }

              const url = '/m/messages/chat/' + chat.id;
              setTimeout(() => {
                this._router.navigate([url], {
                  state: { conversation: chat },
                });
              }, 200);
            } else {
              this.selectConversation(chat);
            }
          }

          if (this.roomId && this.selectedConversation) {
            const conversation = this._chatsList.find((chat) => chat.id === this.selectedConversation.id);
            if (conversation) {
              conversation.count = 0;
            }
          }

          this._fetchGroups();
        },
        error: (error) => {
          log.error('Error fetching conversations:', error);
          this.loadings.conversations = false;
          this.loadings.messages = false;
        },
      });
  }

  /**
   * Subscribe to first user message event from the socket service to update the chat list, fetch conversations and messages when a user sends a message
   * @protected
   */
  protected _subscribeToFirstUserMessage() {
    this._socketService.on(SocketEvents.ChatFirstUserMessage, (data) => {
      this._fetchConversations();
      if (this.roomId) {
        this._fetchConversationMessages(this.selectedConversation);
        this._chatsList.forEach((chat) => {
          if (+chat.id === +data.username) {
            chat.count = 0;
          }
        });
      }
    });
  }

  /**
   * Scroll to the bottom of the chat container
   * @protected
   */
  protected _scrollToBottom(): void {
    if (!this._chatScrollContainer) return;
    try {
      setTimeout(() => {
        this._chatScrollContainer.nativeElement.scrollTop = this._chatScrollContainer.nativeElement.scrollHeight;
      });
    } catch (err) {
      log.error('[ScrollToBottom]:' + err);
    }
  }

  private _getPreviewType(url: string): PreviewType {
    const extension = this._getFileExtension(url).toLowerCase();

    if (!extension) return 'unknown';
    if (this.isImage(url)) return 'image';
    if (['pdf'].includes(extension)) return 'pdf';
    if (['doc', 'docx'].includes(extension)) return 'word';
    if (['xls', 'xlsx', 'csv'].includes(extension)) return 'excel';
    if (['ppt', 'pptx'].includes(extension)) return 'ppt';
    if (['txt', 'md', 'markdown', 'log', 'json', 'xml', 'yaml', 'yml'].includes(extension)) return 'txt';
    if (['mp4', 'webm', 'mov', 'avi', 'mkv', 'm3u8'].includes(extension)) return 'video';
    if (['mp3', 'wav', 'ogg', 'm4a', 'flac', 'aac'].includes(extension)) return 'audio';
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) return 'zip';

    return 'unknown';
  }

  /**
   * Filter out media messages from the selected conversation chat messages
   * @private
   */
  private _filterMediaMessages() {
    this.selectedConversationMediaMessages = this.selectedConversationChatMessages.filter((message) => message.message.messageType !== ChatMessageType.Text && message.message.message);
  }

  /**
   * Fetch common groups between the logged in user and the other user
   * @param otherUserId
   * @private
   */
  private _fetchCommonGroups(otherUserId: number) {
    this._useChat
      .getUserCommonGroups(otherUserId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (response: GroupEntity[]) => {
          this.selectedConversationCommonGroups = response;
        },
      });
  }

  /**
   * Fetch conversation messages as a promise
   * @private
   */
  private _fetchConversationMessagesAsPromise(): Promise<ChatMessageEntity[]> {
    this._resetChatInputFields();

    if (!this.selectedConversation || !this.selectedConversation.id) {
      return Promise.resolve([]);
    }
    this._markConversationAsRead(this.selectedConversation);
    return lastValueFrom(this._useChat.getIndividualUserConversationMessages(this.roomId));
  }

  /**
   * Mark conversation as read
   * @param conversation
   * @private
   */
  private _markConversationAsRead(conversation: ChatEntity) {
    this._useChat.markConversationAsRead(conversation.id, conversation.type).pipe(untilDestroyed(this)).subscribe();
  }

  /**
   * Join chat room
   * @param conversation
   * @private
   */
  private _joinChatRoom(conversation: ChatEntity) {
    const payload = {
      sender: GetUserId(),
      room: this.roomId,
    };

    // Remove all previous listeners to avoid duplicate event listeners
    this._socketService.removeAllListeners(SocketEvents.ChatRoomData);

    this._socketService.emit(SocketEvents.JoinChatRoom, payload, (error: any) => {
      if (error) {
        log.error('Error joining chat room:', error);
      } else {
        log.info('Joined chat room successfully');
      }
    });
    this._socketService.on(SocketEvents.ChatRoomData, (data) => {
      // log.info('Chat Room Data:', data);
    });

    this._fetchConversationMessages(conversation);
  }

  /**
   * Fetch groups
   * @private
   */
  private _fetchGroups() {
    this._useGroup
      .getGroupsByUser()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (response) => {
          this.groups = response;
          this._chatsList.forEach((chat) => {
            if (chat.type === ChatType.GROUP_CHAT) {
              const group = this.groups.find((group) => group.id === +chat.name);
              if (group) {
                chat.name = group.name;
                chat.groupId = group.id;
                chat.groupImageUrl = group.imageUrl;
              }
            }
          });
          if (this._routeGroupId) {
            const groupChat = this._chatsList.find((chat) => +chat.groupId === +this._routeGroupId && chat.type === ChatType.GROUP_CHAT);
            if (groupChat) {
              if (isMobileView()) {
                this._router.navigate([`/m/messages/chat/${groupChat.id}`], {
                  state: { conversation: groupChat },
                });
              } else {
                this.selectConversation(groupChat);
              }
            } else {
              this._openChatForm();
            }
          }

          if (this._routeUserId) {
            const userChat = this._chatsList.find((chat) => +chat.id === +this._routeUserId && chat.type === ChatType.PERSONAL_CHAT);
            if (userChat) {
              if (isMobileView()) {
                this._router.navigate([`/m/messages/chat/${userChat.id}`], {
                  state: { conversation: userChat },
                });
              } else {
                this.selectConversation(userChat);
              }
            } else {
              this._openChatForm();
            }
          }
          this.filteredConversationsList = this._chatsList;
          this.loadings.conversations = false;
        },
      });
  }

  /**
   * Fetch group participants
   * @param groupId
   * @private
   */
  private _fetchGroupParticipants(groupId: number) {
    this._useGroup
      .getGroupParticipantsUsers(groupId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (response) => {
          this.selectedConversationGroupParticipants = response;
        },
      });
  }

  private _openChatForm() {
    if (!this.chatCreatePermission) return;

    const $service = isMobileView() ? this._bottomSheetService : this._dialogService;
    const data = {
      existingConversations: this.filteredConversationsList,
      createGroupChatId: this._routeGroupId,
      createPersonalChatId: this._routeUserId,
    };
    const options = !isMobileView()
      ? {
          size: 'md',
          closeButton: false,
          windowClass: 'dialog-overflow-unset',
          data,
        }
      : {
          height: '65%',
          showCloseButton: false,
          data,
        };

    $service
      .open(ChatFormComponent, options)
      .afterClosed$.pipe(untilDestroyed(this))
      .subscribe({
        next: (result) => {
          if (result) {
            this._socketService.emit(SocketEvents.ChatMessageSent, true);
            this.fetchConversationsAndSelectFirstConversation();
          }
        },
      });
  }

  /**
   * Reset chat input fields
   * @private
   */
  private _resetChatInputFields() {
    this.selectedAttachment = null;
    this.selectedImagePreviewUrl = null;
    this.messageInput = '';
  }

  /**
   * Get file extension
   * @param url
   * @private
   */
  private _getFileExtension(url: string): string {
    if (!url) return '';
    const parts = url.split('.');
    if (parts.length > 1) {
      return parts.pop()?.split('?')[0] || ''; // Take the last part before any query parameters
    }
    return '';
  }
}
